/**
 * model-definition-registry.ts
 *
 * Model Definition Catalog Registry, GGUF Binary Validation,
 * SHA-256 Deduplication, Legacy Migration, and Safe Uninstall Guards.
 */

import {
  ModelDefinition,
  ModelInUseError,
  ModelAssignedError,
  DuplicateModelError,
} from '../contracts/model-orchestration-contracts.js';
import { OrchestrationStore } from '../storage/orchestration-store.js';
import { ModelRegistryService } from '../../llm/model-registry-service.js';

export class ModelDefinitionRegistry {
  constructor(private readonly store: OrchestrationStore) {}

  /**
   * Initializes the registry, seeds default models if empty,
   * and non-destructively migrates legacy active_local_ai_model key.
   */
  async init(): Promise<void> {
    const existing = await this.store.getModels();
    if (existing.length === 0) {
      // Seed default Needle 2 and Gemma 2B definitions
      const defaultNeedle: ModelDefinition = {
        id: 'needle-2-q4km',
        name: 'Needle 2 Schema Extractor Engine',
        kind: 'needle2',
        supportsMultiInstance: true,
        format: 'gguf',
        filePath: '/models/needle-2-q4_k_m.gguf',
        fileSizeBytes: 1200000000,
        sha256: 'a1b2c3d4e5f67890123456789abcdef0123456789abcdef0123456789abcdef0',
        estimatedRamMb: 1200,
        estimatedVramMb: 0,
        contextLength: 4096,
        quantization: 'Q4_K_M',
        capabilities: ['schema-extraction', 'tool-calling', 'field-classification'],
        metadataTrust: 'verified',
        status: 'available',
        createdAt: new Date().toISOString(),
      };

      const defaultGemma: ModelDefinition = {
        id: 'gemma-2-2b-it',
        name: 'Gemma 2B IT (Quantized)',
        kind: 'llm',
        supportsMultiInstance: false,
        format: 'gguf',
        filePath: '/models/gemma-2-2b-it-Q4_K_M.gguf',
        fileSizeBytes: 1650000000,
        sha256: 'b2c3d4e5f67890123456789abcdef0123456789abcdef0123456789abcdef01',
        estimatedRamMb: 1650,
        estimatedVramMb: 2000,
        contextLength: 4096,
        quantization: 'Q4_K_M',
        capabilities: ['general-reasoning', 'rag-grounding', 'chat'],
        metadataTrust: 'verified',
        status: 'available',
        createdAt: new Date().toISOString(),
      };

      await this.store.saveModel(defaultNeedle);
      await this.store.saveModel(defaultGemma);
    }

    // Check for legacy localStorage migration
    if (typeof localStorage !== 'undefined') {
      try {
        const legacyModel = localStorage.getItem('active_local_ai_model');
        if (legacyModel) {
          await this.migrateFromLegacyKey(legacyModel);
        }
      } catch {}
    }
  }

  async migrateFromLegacyKey(legacyModelId: string): Promise<void> {
    const existing = await this.getModel(legacyModelId);
    if (!existing) {
      const isNeedle = legacyModelId.includes('needle');
      const migrated: ModelDefinition = {
        id: legacyModelId,
        name: isNeedle ? 'Needle 2 (Migrated)' : 'Gemma 2B IT (Migrated)',
        kind: isNeedle ? 'needle2' : 'llm',
        supportsMultiInstance: isNeedle,
        format: 'gguf',
        filePath: `/models/${legacyModelId}.gguf`,
        fileSizeBytes: isNeedle ? 1200000000 : 1650000000,
        sha256: `migrated-${legacyModelId}-sha256`,
        estimatedRamMb: isNeedle ? 1200 : 1650,
        estimatedVramMb: isNeedle ? 0 : 2000,
        capabilities: isNeedle ? ['schema-extraction'] : ['general-reasoning'],
        metadataTrust: 'locally-overridden',
        status: 'available',
        createdAt: new Date().toISOString(),
      };
      await this.store.saveModel(migrated);
    }
  }

  async getModel(id: string): Promise<ModelDefinition | undefined> {
    const models = await this.store.getModels();
    return models.find((m) => m.id === id);
  }

  async getAllModels(): Promise<readonly ModelDefinition[]> {
    return this.store.getModels();
  }

  async importModel(
    definition: Omit<ModelDefinition, 'metadataTrust' | 'status' | 'createdAt'>,
    binaryBuffer?: Buffer | Uint8Array
  ): Promise<ModelDefinition> {
    // 1. Check for duplicate SHA-256
    const models = await this.store.getModels();
    const duplicate = models.find((m) => m.sha256 === definition.sha256 && m.id !== definition.id);
    if (duplicate) {
      await this.store.appendEvent({
        eventId: `evt-import-fail-${Date.now()}`,
        kind: 'model.import_failed',
        payload: { modelId: definition.id, reason: 'Duplicate SHA-256 hash', existingId: duplicate.id },
        originId: 'registry',
        sequence: 0,
        occurredAt: new Date().toISOString(),
      });
      throw new DuplicateModelError(definition.sha256, duplicate.id);
    }

    // 2. Validate GGUF binary structure if buffer provided
    let metadataTrust: ModelDefinition['metadataTrust'] = 'trusted';
    if (binaryBuffer && definition.format === 'gguf') {
      const ggufResult = ModelRegistryService.validateGgufBinary(binaryBuffer);
      if (ggufResult.valid) {
        metadataTrust = 'verified';
      } else {
        metadataTrust = 'locally-overridden';
      }
    }

    const model: ModelDefinition = {
      ...definition,
      metadataTrust,
      status: 'available',
      createdAt: new Date().toISOString(),
    };

    await this.store.saveModel(model);
    await this.store.appendEvent({
      eventId: `evt-import-${Date.now()}`,
      kind: 'model.imported',
      payload: { modelId: model.id, name: model.name, sha256: model.sha256 },
      originId: 'registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });

    return model;
  }

  async validateModel(modelId: string, expectedSha256?: string): Promise<{ valid: boolean; reason?: string }> {
    const model = await this.getModel(modelId);
    if (!model) {
      return { valid: false, reason: `Model ${modelId} not found in catalog` };
    }

    if (expectedSha256 && model.sha256 !== expectedSha256) {
      await this.store.appendEvent({
        eventId: `evt-val-fail-${Date.now()}`,
        kind: 'model.validation_failed',
        payload: { modelId, reason: 'SHA-256 digest mismatch' },
        originId: 'registry',
        sequence: 0,
        occurredAt: new Date().toISOString(),
      });
      return { valid: false, reason: 'SHA-256 digest mismatch' };
    }

    await this.store.appendEvent({
      eventId: `evt-val-ok-${Date.now()}`,
      kind: 'model.validated',
      payload: { modelId, sha256: model.sha256 },
      originId: 'registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });

    return { valid: true };
  }

  async rescanModels(existingFilePaths: readonly string[]): Promise<void> {
    const models = await this.store.getModels();
    for (const m of models) {
      if (m.status === 'available' && !existingFilePaths.includes(m.filePath)) {
        const missingModel: ModelDefinition = {
          ...m,
          status: 'missing',
        };
        await this.store.saveModel(missingModel);
      }
    }
  }

  async uninstall(modelId: string, force: boolean = false): Promise<void> {
    const model = await this.getModel(modelId);
    if (!model) return;

    // 1. Guard against active runtimes
    const runtimes = await this.store.getRuntimes();
    const activeRuntimes = runtimes.filter(
      (r) => r.modelId === modelId && (r.status === 'active' || r.status === 'starting' || r.status === 'draining')
    );
    if (activeRuntimes.length > 0) {
      throw new ModelInUseError(modelId, activeRuntimes.length);
    }

    // 2. Guard against existing assignments
    const assignments = await this.store.getAssignments();
    const boundAssignments = assignments.filter(
      (a) => a.preferredModelId === modelId || a.fallbackModelIds.includes(modelId)
    );
    if (boundAssignments.length > 0 && !force) {
      throw new ModelAssignedError(modelId, boundAssignments.length);
    }

    // 3. Mark uninstalled / remove
    await this.store.deleteModel(modelId);
    await this.store.appendEvent({
      eventId: `evt-uninst-${Date.now()}`,
      kind: 'model.uninstalled',
      payload: { modelId, force, affectedAssignments: boundAssignments.map((a) => a.id) },
      originId: 'registry',
      sequence: 0,
      occurredAt: new Date().toISOString(),
    });
  }
}
